import { useContext, useEffect, useRef, useState } from "react";
import ProjectInfo from "../../models/projectInfo";
import TableColum from "../../models/tableColumn";
import MapLayer from "../../models/mapLayer";
import MapLayerType from "../../models/mapLayerType";
import User from "../../models/user";
import UserContext from "../../contexts/userContext";
import ThematicMapLayer from "../../models/thematicMapLayer";
import { Tooltip } from "@mui/material";
import MapLayerShareIcon from "./MapLayerShareIcon";
import SegmentInfoEdit from "./segment-info/SegmentInfoEdit";
import SegmentInfo from "../../models/segmentInfo";
import MapLayerEdit from "./MapLayerEdit";
import GeospatialMapLayer from "../../models/geospatialMapLayer";
import GeospatialLayerIcon from "@mui/icons-material/Map";
import NetworkLayerIcon from "@mui/icons-material/Language";
import CurrentRoadLayerIcon from "@mui/icons-material/Route";
import ThematicMapLayerIcon from "../../assets/images/thematic-regions-icon.svg";
import Sortable from "sortablejs";
import SwapVertIcon from "@mui/icons-material/SwapVert";
// import DragHandleIcon from "@mui/icons-material/DragHandle";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";

const MapLayers: React.FC<{
  project: ProjectInfo;
  mapLayers: MapLayer[];
  tableColumns: TableColum[];
  segmentInfo: SegmentInfo;
  onCreateMapLayer: any;
  onUpdateMapLayer: any;
  onDeleteMapLayer: any;
  onUpdateMapLayerVisibility: any;
  onUpdateLayersOrder: any;
  onUpdateSegmentInfo: any;
}> = ({
  project,
  mapLayers,
  tableColumns,
  segmentInfo,
  onUpdateMapLayer,
  onCreateMapLayer,
  onDeleteMapLayer,
  onUpdateMapLayerVisibility,
  onUpdateLayersOrder,
  onUpdateSegmentInfo,
}) => {
  const user = useContext<User | undefined>(UserContext);

  const [showEditMapLayer, setShowEditMapLayer] = useState<boolean>(false);

  const [mapLayer, setMapLayer] = useState<MapLayer>(new MapLayer());

  const [showEditSegmentInfo, setShowEditSegmentInfo] =
    useState<boolean>(false);

  const layersListRef = useRef<HTMLUListElement>(null);

  const [isReorderLayers, setIsReorderLayers] = useState<boolean>(false);

  useEffect(() => {
    if (!layersListRef.current) {
      return;
    }

    // console.log("Sortable-init");

    const sortable = new Sortable(layersListRef.current, {
      animation: 150,
      disabled: !isReorderLayers,
      dataIdAttr: "data-id",
      onEnd: async (evt) => {
        const { item, from, oldIndex, newIndex } = evt;

        if (
          oldIndex === undefined ||
          newIndex === undefined ||
          oldIndex === newIndex
        ) {
          return;
        }

        const orderedMapLayerIds = sortable.toArray().map(Number);

        // undo Sortable's DOM change and let React be the only thing that reorders the list

        // put the element back where React expects it
        from.removeChild(item);
        from.insertBefore(item, from.children[oldIndex] ?? null);

        // let React re-render in the new order
        await onUpdateLayersOrder(orderedMapLayerIds);
      },
    });

    return () => {
      // console.log("Sortable-destory");
      sortable.destroy();
    };
  }, [isReorderLayers, onUpdateLayersOrder]);

  const onCreateMapLayerProxy = async (mapLayer: MapLayer) => {
    setShowEditMapLayer(false);
    await onCreateMapLayer(mapLayer);
  };

  const onUpdateMapLayerProxy = async (mapLayer: MapLayer) => {
    setShowEditMapLayer(false);
    await onUpdateMapLayer(mapLayer);
  };

  const onDeleteMapLayerProxy = async (mapLayer: MapLayer) => {
    setShowEditMapLayer(false);
    await onDeleteMapLayer(mapLayer);
  };

  const onCancelEditMapLayer = () => {
    setShowEditMapLayer(false);
  };

  const onNewLayer = () => {
    closeOpenPanels();

    const newMapLayer = new MapLayer();
    newMapLayer.idProject = project.idProject;

    newMapLayer.isVisible = true;

    if (user) {
      newMapLayer.idUser = user.idUser;
    }

    // default

    if (project.hasThematicMaps) {
      newMapLayer.thematicMapLayer = new ThematicMapLayer();
      newMapLayer.mapLayerType = MapLayerType.ThematicLayer;
    } else {
      newMapLayer.geospatialMapLayer = new GeospatialMapLayer();
      newMapLayer.mapLayerType = MapLayerType.GeospatialLayer;
    }

    setMapLayer(newMapLayer);

    setShowEditMapLayer(true);
  };

  const onEditMapLayer = (event: any, mapLayer: MapLayer) => {
    event.stopPropagation();
    setMapLayer(mapLayer);

    closeOpenPanels();

    setShowEditMapLayer(true);
  };

  const onLayerClick = async (event: any, mapLayer: MapLayer) => {
    await onUpdateMapLayerVisibility(mapLayer, !mapLayer.isVisible);
  };

  const onEditEditSegmentInfo = (event: any) => {
    closeOpenPanels();

    setShowEditSegmentInfo(true);
  };

  const onUpdateSegmentInfoProxy = (segmentInfo: SegmentInfo) => {
    setShowEditSegmentInfo(false);

    onUpdateSegmentInfo(segmentInfo);
  };

  const onCancelEditSegmentInfo = () => {
    setShowEditSegmentInfo(false);
  };

  const closeOpenPanels = () => {
    setShowEditMapLayer(false);
    setShowEditSegmentInfo(false);
  };

  const canEditMapLayer = (mapLayer: MapLayer) => {
    if (
      mapLayer.mapLayerType === MapLayerType.NetworkLayer ||
      mapLayer.mapLayerType === MapLayerType.CurrentRoadLayer
    ) {
      return false;
    }

    if (mapLayer.mapLayerType === MapLayerType.GeospatialLayer) {
      const isSharedByOtherUser =
        mapLayer.parentMapLayer &&
        mapLayer.parentMapLayer.idUser !== user?.idUser;
      if (isSharedByOtherUser) {
        return false;
      }
    }

    return true;
  };

  const onIsReorderLayersChanged = (event: any) => {
    setIsReorderLayers((prev) => !prev);
  };

  const onResetLayersOrderToDefault = async (event: any) => {
    // Default Order:
    //
    // Geospatial
    //    Shared & User - Alphabetical
    // Network
    // Current Road
    // Thematic
    //    Shared & User - Alphabetical

    const orderedMapLayers: MapLayer[] = [];

    let geospatialLayers = mapLayers.filter(
      (ml) => ml.mapLayerType === MapLayerType.GeospatialLayer,
    );

    geospatialLayers.sort((a, b) => a.layerName.localeCompare(b.layerName));

    for (const ml of geospatialLayers) {
      orderedMapLayers.push(ml);
    }

    const networkLayer = mapLayers.find(
      (ml) => ml.mapLayerType === MapLayerType.NetworkLayer,
    );
    if (networkLayer) {
      orderedMapLayers.push(networkLayer);
    }

    const currentRoadLayer = mapLayers.find(
      (ml) => ml.mapLayerType === MapLayerType.CurrentRoadLayer,
    );
    if (currentRoadLayer) {
      orderedMapLayers.push(currentRoadLayer);
    }

    let thematicLayers = mapLayers.filter(
      (ml) => ml.mapLayerType === MapLayerType.ThematicLayer,
    );

    thematicLayers.sort((a, b) => a.layerName.localeCompare(b.layerName));

    for (const ml of thematicLayers) {
      orderedMapLayers.push(ml);
    }

    const orderedMapLayerIds = orderedMapLayers.map((ml) => ml.idMapLayer);

    await onUpdateLayersOrder(orderedMapLayerIds);
  };

  return (
    <div
      className={
        "dropdown-menu-border " +
        (showEditMapLayer || showEditSegmentInfo ? "d-flex" : "")
      }
      style={{
        padding: "10px",
        background: "white",
      }}
    >
      <div className="dropdown-section" style={{ minWidth: "250px" }}>
        <div className="mb-1 d-flex justify-content-between align-items-center">
          <h4 style={{ margin: "0px" }}>Map Layers</h4>
          <div className="d-flex align-items-center">
            {isReorderLayers && (
              <div>
                <Tooltip title="Reset Layers Order to Default">
                  <div
                    className="image-button-link"
                    style={{ cursor: "pointer" }}
                    onClick={onResetLayersOrderToDefault}
                  >
                    <SortByAlphaIcon />
                  </div>
                </Tooltip>
              </div>
            )}
            <div className="me-1">
              <Tooltip title="Reorder Layers">
                <div
                  className={!isReorderLayers ? "image-button-link" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={onIsReorderLayersChanged}
                >
                  <SwapVertIcon />
                </div>
              </Tooltip>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={onNewLayer}
            >
              New Layer
            </button>
          </div>
        </div>
        <ul
          style={{
            listStyle: "none",
            margin: "0px",
            padding: "0px",
          }}
          ref={layersListRef}
        >
          {mapLayers.map((mapLayer, index) => (
            <li key={index.toString()} data-id={mapLayer.idMapLayer}>
              <button
                className={
                  "dropdown-item-li-button dropdown-item" +
                  (mapLayer.isVisible ? " active" : "")
                }
                type="button"
                onClick={(e) => onLayerClick(e, mapLayer)}
              >
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center">
                    {isReorderLayers && (
                      <DragIndicatorIcon style={{ fontSize: "20px" }} />
                    )}
                    <div>{mapLayer.layerName}</div>
                    {mapLayer.mapLayerType === MapLayerType.NetworkLayer && (
                      <Tooltip
                        className="ms-2 align-self-center"
                        title="Network Layer"
                      >
                        <NetworkLayerIcon
                          style={{
                            fontSize: "16px",
                            marginTop: "2px",
                          }}
                        />
                      </Tooltip>
                    )}
                    {mapLayer.mapLayerType ===
                      MapLayerType.CurrentRoadLayer && (
                      <Tooltip
                        className="ms-2 align-self-center"
                        title="Current Road Layer"
                      >
                        <CurrentRoadLayerIcon
                          style={{
                            fontSize: "16px",
                            marginTop: "2px",
                          }}
                        />
                      </Tooltip>
                    )}
                    {mapLayer.mapLayerType === MapLayerType.ThematicLayer && (
                      <Tooltip
                        className="ms-2 align-self-center"
                        title="Thematic Layer"
                      >
                        <div>
                          <img
                            src={ThematicMapLayerIcon}
                            width={24}
                            height={24}
                            alt=""
                          />
                        </div>
                      </Tooltip>
                    )}
                    {mapLayer.mapLayerType === MapLayerType.GeospatialLayer && (
                      <Tooltip
                        className="ms-2 align-self-center"
                        title="Geospatial Layer"
                      >
                        <GeospatialLayerIcon
                          style={{
                            fontSize: "16px",
                            marginTop: "2px",
                          }}
                        />
                      </Tooltip>
                    )}
                    <MapLayerShareIcon mapLayer={mapLayer} />
                  </div>
                  {canEditMapLayer(mapLayer) && (
                    <div
                      style={{ marginLeft: "15px" }}
                      onClick={(event) => onEditMapLayer(event, mapLayer)}
                    >
                      <Tooltip
                        className="ms-2 align-self-center"
                        title="Layer Settings"
                      >
                        <i className="navbar-nav-fa fa fa-cog fa-lg"></i>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>

        <div className="d-flex justify-content-end">
          {user?.isPortalAdmin && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="btn btn-sm btn-primary mt-3"
                onClick={onEditEditSegmentInfo}
              >
                Segment Info &nbsp;
                <i className="navbar-nav-fa fa fa-cog fa-lg"></i>
              </button>
              <div
                className="badge bg-secondary me-2"
                style={{
                  position: "absolute",
                  left: "-10px",
                  top: "4px",
                  border: "1px solid white",
                }}
              >
                PORTAL ADMIN
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditMapLayer && (
        <div className="editor-separator ms-2">
          <MapLayerEdit
            project={project}
            tableColumns={tableColumns}
            mapLayer={
              user?.isPortalAdmin &&
              mapLayer.parentMapLayer &&
              mapLayer.parentMapLayer.isDefault
                ? mapLayer.parentMapLayer
                : mapLayer
            }
            isNew={mapLayer.idMapLayer === 0}
            onCreateMapLayer={onCreateMapLayerProxy}
            onUpdateMapLayer={onUpdateMapLayerProxy}
            onDeleteMapLayer={onDeleteMapLayerProxy}
            onCancel={onCancelEditMapLayer}
          />
        </div>
      )}

      {showEditSegmentInfo && (
        <div className="editor-separator ms-2">
          <SegmentInfoEdit
            segmentInfo={segmentInfo}
            tableColumns={tableColumns}
            onUpdate={onUpdateSegmentInfoProxy}
            onCancel={onCancelEditSegmentInfo}
          />
        </div>
      )}
    </div>
  );
};

export default MapLayers;
